import { Component, signal, computed, effect, DestroyRef, inject, NgZone, ChangeDetectorRef, HostListener } from '@angular/core';
import { Commands } from '../../services/commands/commands';
import { Events } from '../../services/events/events';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EndGame } from '../end-game/end-game';

@Component({
  selector: 'app-leader-board',
  imports: [EndGame],
  templateUrl: './leader-board.html',
  styleUrl: './leader-board.css'
})
export class LeaderBoard {

  // PROPERTIES

  private destroyRef = inject(DestroyRef);
  private timer: number | null = null;
  private is_round_doubled: boolean = false;

  // SIGNALS

  private time = signal<number>(60000); // ms

  protected passes = signal<number>(3);
  protected doubles = signal<number>(2);
  protected points = signal<number>(0);
  
  protected round_status = signal<{
    ready: boolean,   // ready to start
    active: boolean,  // started
    pending: boolean  // waiting for right/wrong answer evaluation
  }>({
    ready: true,
    active: false,
    pending: false
  });
  
  // COMPUTED SIGNALS

  protected is_game_active = computed<boolean>(() =>
    this.time() > 0
  );

  protected rounded_time = computed<number>(() =>
      Math.ceil(this.time() / 1000)
  );
  
  protected are_passes_available = computed<boolean>(() =>
    this.passes() > 0
  );

  protected are_doubles_available = computed<boolean>(() =>
    this.doubles() > 0
  );

  protected are_doubles_allowed = computed<boolean>(() =>
    this.points() >= 2
  );

  // EVENT LISTENERS

  private playerLeftListener(): void{

    this.events.onPlayerLeft()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          // we cannot rely on is_game_active signal
          // because of possible small sync gap between the time of the 2 players
          if (data.is_game_ended) return;

          alert("The connection to a player is lost. The match has ended.");
          this.exitBoard();

        }
      });

  }

  // COMMANDS & ROUND MANAGEMENT

  // from ready to active round status
  public startRound(is_double_mode: boolean): void{

    // 100 ms = estimated round-trip time
    // due to that, when the BE is hosted locally, we may experience a "longer" second at the beginning of the round
    // don't worry! this doesn't happen on a remote server :)
    const sync_time = Date.now() + 100;

    this.commands.startRound(sync_time, is_double_mode, () => {

      this.startTimer(sync_time);

      this.round_status.update(value => ({

        ...value,
        ready: false,
        active: true

      }));

    });

    // handle doubles
    if (is_double_mode){

      this.doubles.update(value => value - 1);
      this.is_round_doubled = true;

    }

    else this.is_round_doubled = false;

  }

  // from active to pending round status
  public endRound(): void{

    this.stopTimer();

    this.commands.endRound(this.time(), () => {

      this.round_status.update(value => ({
        
        ...value,
        active: false,
        pending: true

      }));

    });

  }

  // guessed: from pending to ready round status 
  public guessWord(): void{

    this.is_round_doubled
      ? this.points.update(value => value + 2)
      : this.points.update(value => value + 1);
    
    this.resetRound();

  }

  // missed: from pending to ready round status 
  public missWord(): void{

    this.is_round_doubled
      ? this.points.update(value => value - 2)
      : this.points() && this.points.update(value => value - 1);
    
    this.resetRound();

  }

  // passed: from pending to ready round status 
  public passWord(): void{

    this.commands.passWord();

    this.passes.update(value => value - 1);

    this.resetRound();

  }

  // from active to end game status
  private endGame(): void{

    this.commands.endGame();

  }

  private alertPointsUpdate(points: number): void{

    this.commands.updatePoints(points);

  }

  // TIME MANAGEMENT

  private startTimer(sync_time: number): void{

    // No signal should be used here, as all the logic needs to be synchronous.
    // In fact, the countodown function is brittle, as it is called around every 16 ms.

    let curr_time = sync_time; 
    let curr_seconds = this.rounded_time();

    const countdown = () => {

      // base case
      if (curr_seconds === 0) return;

      // new time calculation
      // Math.max is needed in case the expected round trip time is bigger then the expected one
      const time = Date.now();
      this.time.update(value => value - Math.max(0, time - curr_time));
      curr_time = time;

      // trigger game end if time reaches 0
      if (this.time() <= 0) this.endGame();

      // new seconds calculation
      const seconds = this.rounded_time();

      if (seconds < curr_seconds){

        curr_seconds = seconds;
        this.change_detector_ref.detectChanges();

      }

      // recursion
      this.timer = requestAnimationFrame(countdown);

    };

    // run timer outside angular to improve performances
    this.ng_zone.runOutsideAngular(() => {
      this.timer = requestAnimationFrame(countdown);
    });

  }

  private stopTimer(): void{

    this.timer && cancelAnimationFrame(this.timer);
    this.timer = null;

  }

  // UTILITIES

  public exitBoard(): void{

    const room_id = this.commands.getRoomId();
    this.router.navigate([`/mind-link/waiting-room/${room_id}`]);

  }

  private resetRound(): void{

    this.round_status.update(value => ({

      ...value,
      pending: false,
      ready: true

    }));

  }

  // HOST EVENTS

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent){

    if (this.is_game_active())
      event.returnValue = true;

  }

  // LIFECYCLE

  constructor(
    private router: Router,
    private commands: Commands,
    private events: Events,
    private ng_zone: NgZone,
    private change_detector_ref: ChangeDetectorRef
  ){

    effect(() => {
      this.alertPointsUpdate(this.points());
    });

  }

  ngOnInit(){
    this.playerLeftListener();
  }

  ngOnDestroy(){
    this.stopTimer();
  }

}
